import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HiOutlineCheckCircle, HiOutlineClock } from "react-icons/hi";

import {
  getSurveillanceStatusContext,
  markSurveillanceDetailedSearchDone,
  type SurveillanceStatusContext,
} from "../../api/surveillance";

export const SURVEILLANCE_DETAILED_SEARCH_FORM_ID =
  "surveillance-detailed-search-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SurveillanceDetailedSearchView({
  leadId,
  onSuccess,
}: Props) {
  const [ctx, setCtx] = useState<SurveillanceStatusContext | null>(null);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setCtx(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSurveillanceStatusContext(leadId)
      .then((data) => {
        if (!cancelled) setCtx(data);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load status data"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    if (ctx?.detailedSearchDone) {
      onSuccess?.(leadId);
      return;
    }

    setSaving(true);
    try {
      const data = await markSurveillanceDetailedSearchDone(leadId);
      setCtx(data);
      toast.success("Detailed search marked as done.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to mark search as done"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-5 w-full py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">
          Please complete the Discussion steps to access Status.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-5 w-full py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  const isDone = ctx?.detailedSearchDone ?? false;
  const doneAt = ctx?.detailedSearchDoneAt ?? null;

  return (
    <form
      id={SURVEILLANCE_DETAILED_SEARCH_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Info card */}
      <div className="flex flex-col gap-1.5">
        <p
          className="font-semibold text-[#101111]"
          style={{ fontSize: "14px", lineHeight: "100%", letterSpacing: "0.01em" }}
        >
          Detailed Search
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{ fontSize: "12px", lineHeight: "20px", letterSpacing: "0.04em" }}
        >
          Monthly trademark surveillance search performed by staff.
        </p>
      </div>

      <div
        className="flex flex-col gap-3 p-4 rounded-[10px] border border-[#EEEEEE] bg-[#F7F7F7]"
        style={{ fontSize: "12px", lineHeight: "20px" }}
      >
        <div className="flex items-start gap-3">
          <HiOutlineClock className="w-5 h-5 text-[#6D6D6D] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="flex flex-col gap-0.5">
            <p className="font-medium text-[#222222]" style={{ fontSize: "12px" }}>
              About this step
            </p>
            <p className="text-[#6D6D6D]">
              Our staff will be manually doing the search at the end of every month.
              Once the search is complete, mark it as done to proceed to the next step.
            </p>
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className="flex flex-col gap-3">
        <p
          className="font-medium text-[#222222]"
          style={{ fontSize: "12px", lineHeight: "20px" }}
        >
          Search Status
        </p>

        {isDone ? (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-[8px] border border-[#D1FAE5] bg-[#F0FDF4]">
            <HiOutlineCheckCircle className="w-5 h-5 text-[#16A34A] flex-shrink-0" strokeWidth={1.5} />
            <div className="flex flex-col gap-0.5">
              <p
                className="font-medium text-[#16A34A]"
                style={{ fontSize: "12px", lineHeight: "20px" }}
              >
                Marked as Done
              </p>
              {doneAt && (
                <p
                  className="text-[#6D6D6D]"
                  style={{ fontSize: "11px", lineHeight: "16px" }}
                >
                  Completed on {formatDateTime(doneAt)}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-[8px] border border-[#EEEEEE] bg-white">
              <div className="w-2 h-2 rounded-full bg-[#F59E0B] flex-shrink-0" />
              <p
                className="font-normal text-[#6D6D6D]"
                style={{ fontSize: "12px", lineHeight: "20px" }}
              >
                Pending — search has not been marked as done yet
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="self-start flex items-center gap-2 px-4 py-2 h-9 text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Marking…" : "Mark as Done"}
            </button>
          </div>
        )}
      </div>
    </form>
  );
}
